import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface JobResultStatisticsProps {
  comments: any[];
}

export default function JobResultStatistics({ comments }: JobResultStatisticsProps) {
  const isMobile = useMediaQuery('(max-width:600px)');

  if (comments.length === 0) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        Nincsenek statisztikai adatok
      </Typography>
    );
  }

  const scoreCounts = comments.reduce((acc: Record<number, number>, comment) => {
    const score = comment.score;
    acc[score] = (acc[score] || 0) + 1;
    return acc;
  }, {});

  const dataset = Object.entries(scoreCounts).map(([score, count]) => ({
    score,
    count,
  }));

  const timeSortedComments = [...comments].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const cumulativeScores = timeSortedComments.map((_, idx) => {
    const slice = timeSortedComments.slice(0, idx + 1);
    const avg = slice.reduce((acc, cur) => acc + cur.score, 0) / slice.length;
    return Number(avg.toFixed(2));
  });
  const timeLabels = timeSortedComments.map((_, idx) => `#${idx + 1}`);

  const bins = {
    '0-2': 0,
    '3-5': 0,
    '6-8': 0,
    '9-10': 0,
  };
  comments.forEach((comment) => {
    const score = comment.score;
    if (score <= 2) bins['0-2']++;
    else if (score <= 5) bins['3-5']++;
    else if (score <= 8) bins['6-8']++;
    else bins['9-10']++;
  });
  const histogramDataset = Object.entries(bins).map(([range, count]) => ({
    range,
    count,
  }));

  const categoryCounts = { Negative: 0, Neutral: 0, Positive: 0 };
  comments.forEach((comment) => {
    const score = comment.score;
    if (score <= 3) categoryCounts.Negative++;
    else if (score <= 6) categoryCounts.Neutral++;
    else categoryCounts.Positive++;
  });
  const pieData = [
    { id: 0, value: categoryCounts.Negative, label: 'Negatív' },
    { id: 1, value: categoryCounts.Neutral, label: 'Semleges' },
    { id: 2, value: categoryCounts.Positive, label: 'Pozitív' },
  ];

  const topPositive = [...comments].sort((a, b) => b.score - a.score).slice(0, 3);
  const topNegative = [...comments].sort((a, b) => a.score - b.score).slice(0, 3);

  const renderTitleWithInfo = (title: string, tooltipText: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Typography variant="h6">{title}</Typography>
      <Tooltip title={tooltipText} arrow>
        <IconButton size="small">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* --- Eredeti BarChart --- */}
      <Box sx={{ my: 4 }}>
        {renderTitleWithInfo(
          'Pontszámok gyakorisága',
          'Megmutatja, hogy hány komment kapott adott pontszámot 0 és 10 között.'
        )}
        <BarChart
          dataset={dataset}
          yAxis={[{ scaleType: 'band', dataKey: 'score' }]}
          series={[{ dataKey: 'count', label: 'Kommentek száma' }]}
          layout="horizontal"
          height={400}
        />
      </Box>

      {/* 1. Átlagos pontszám időbeli trendje */}
      <Box>
        {renderTitleWithInfo(
          'Átlagos pontszám időbeli változása',
          'A kommentek feldolgozási sorrendjében mutatja az átlagos pontszám alakulását.'
        )}
        <LineChart
          xAxis={[{ scaleType: 'point', data: timeLabels }]}
          series={[{ data: cumulativeScores, label: 'Átlagos pontszám' }]}
          height={300}
        />
      </Box>

      {/* 2. Pontszám eloszlás */}
      <Box>
        {renderTitleWithInfo(
          'Pontszám eloszlás',
          'A kommentek pontszámait 4 kategóriába csoportosítva mutatja be az eloszlást.'
        )}
        <BarChart
          dataset={histogramDataset}
          yAxis={[{ scaleType: 'band', dataKey: 'range' }]}
          series={[{ dataKey: 'count', label: 'Kommentek száma' }]}
          layout="horizontal"
          height={300}
        />
      </Box>

      {/* 3. Pie chart – mobilon a legend alul */}
      <Box>
        {renderTitleWithInfo(
          'Kategóriák megoszlása',
          'Arányosan mutatja a negatív, semleges és pozitív kommentek számát. 0-3: Negatív, 4-6: Semleges, 7-10: Pozitív'
        )}
        <PieChart
          series={[{ data: pieData }]}
          height={300}
          margin={isMobile ? { bottom: 64 } : { right: 160 }}
          slotProps={{
            legend: {
              hidden: false,
              direction: isMobile ? 'row' : 'column',
              position: isMobile
                ? { vertical: 'bottom', horizontal: 'middle' }
                : { vertical: 'middle', horizontal: 'right' },
              itemMarkWidth: 12,
              itemMarkHeight: 12,
              labelStyle: { fontSize: 12 },
            },
          }}
        />
      </Box>

      {/* 4. Top kommentek */}
      <Box>
        {renderTitleWithInfo('Top pozitív kommentek', 'A legmagasabb pontszámú kommentek.')}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <List>
            {topPositive.map((comment) => (
              <ListItem key={comment.id} alignItems="flex-start">
                <ListItemText primary={comment.comment} secondary={`Pontszám: ${comment.score}`} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {renderTitleWithInfo('Top negatív kommentek', 'A legalacsonyabb pontszámú kommentek.')}
        <Paper variant="outlined">
          <List>
            {topNegative.map((comment) => (
              <ListItem key={comment.id} alignItems="flex-start">
                <ListItemText primary={comment.comment} secondary={`Pontszám: ${comment.score}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    </Box>
  );
}
